import { graphql, Link, useStaticQuery } from 'gatsby';
import Img from 'gatsby-image';
import React, { useState } from 'react';
import styled from 'styled-components';
import { headerTypes } from '../types/propTypes';

const TopNav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-width: 640px) {
    img {
      height: 32px;
    }
  }

  &.nav-scrolled {
    background: rgba(0, 0, 0, 0.65);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    padding: 18px 60px;

    @media (max-width: 768px) {
      padding: 16px 20px;
    }
  }

  &.nav-top {
    background: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    padding: 32px 60px;

    @media (max-width: 768px) {
      padding: 20px 20px;
    }
  }
`;

const NavInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavItem = styled(Link)`
  font-size: 11.5px;
  font-weight: 400;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
  position: relative;
  padding: 4px 0;
  transition: color 0.4s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background: #fff;
    transition: width 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:hover,
  &.active {
    color: #fff;

    &::after {
      width: 100%;
    }
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
`;

const NavCta = styled(Link)`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #fff;
  padding: 11px 24px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  svg {
    width: 14px;
    height: 14px;
    stroke: #fff;
    fill: none;
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: transform 0.3s ease, stroke 0.3s ease;
  }

  &:hover {
    border-color: #f08020;
    color: #f08020;
    svg {
      stroke: #f08020;
      transform: translateX(4px);
    }
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 14px;
  background: rgba(255, 255, 255, 0.15);

  @media (max-width: 900px) {
    display: none;
  }
`;

const Burger = styled.button`
  display: none;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  @media (max-width: 900px) {
    display: flex;
  }

  .lines {
    width: 24px;
    height: 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    span {
      display: block;
      height: 1px;
      background: #fff;
      transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);

      &:nth-child(1) {
        width: 100%;
      }
      &:nth-child(2) {
        width: 50%;
        margin-left: auto;
      }
    }
  }

  &.open .lines span:nth-child(1) {
    transform: rotate(45deg) translate(4.5px, 4.5px);
  }
  &.open .lines span:nth-child(2) {
    width: 100%;
    transform: rotate(-45deg) translate(4.5px, -4.5px);
  }
`;

const MobileMenu = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 32px 0 24px;
    margin-top: 18px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

const MobileLink = styled(Link)`
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  padding: 14px 0;
  transition: color 0.3s ease;

  &:hover,
  &.active {
    color: #fff;
  }
`;

const MobileCta = styled(Link)`
  display: inline-block;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #f08020;
  transition: color 0.3s ease;

  &:hover {
    color: #f4a040;
  }
`;

const headerQuery = graphql`
  {
    logo: file(relativePath: { eq: "logo.png" }) {
      childImageSharp {
        fixed(width: 90) {
          ...GatsbyImageSharpFixed
        }
      }
    }
  }
`;

export default function Header({ location, scrolled }) {
  const data = useStaticQuery(headerQuery);
  const [collapsed, setCollapsed] = useState(true);

  const toggle = () => setCollapsed(!collapsed);
  const close = () => setCollapsed(true);

  const navClass = scrolled || !collapsed ? 'nav-scrolled' : 'nav-top';

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/portfolio', label: 'Projects' },
    { to: '/services', label: 'Services' },
    { to: '/contact', label: 'Contact' },
  ];

  const currentPath = location?.pathname || '';

  return (
    <TopNav className={navClass}>
      <NavInner>
        <Link to="/" onClick={close}>
          <Img id="navLogo" fixed={data.logo.childImageSharp.fixed} />
        </Link>

        <NavLinks>
          {navLinks.map(link => (
            <NavItem
              key={link.to}
              to={link.to}
              className={currentPath === link.to ? 'active' : ''}
            >
              {link.label}
            </NavItem>
          ))}
        </NavLinks>

        <NavRight>
          <NavCta to="/contact">
            Book Consultation
            <svg viewBox="0 0 24 24">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </NavCta>
          <Burger onClick={toggle} className={!collapsed ? 'open' : ''}>
            <div className="lines">
              <span />
              <span />
            </div>
          </Burger>
        </NavRight>
      </NavInner>

      {!collapsed && (
        <MobileMenu>
          {navLinks.map(link => (
            <MobileLink
              key={link.to}
              to={link.to}
              className={currentPath === link.to ? 'active' : ''}
              onClick={close}
            >
              {link.label}
            </MobileLink>
          ))}
          <MobileCta to="/contact" onClick={close}>
            Book Consultation
          </MobileCta>
        </MobileMenu>
      )}
    </TopNav>
  );
}

Header.propTypes = headerTypes;
